import { call, select, take, takeEvery, fork, put } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';
import {
  VALIDATE_CREDENTIALS_SUCCESS,
  VALIDATE_TOKEN_SUCCESS,
  LOGOUT,
  SIGNUP_SUCCESS,
  AUTH_SUCCESS
} from 'JS/actionCreators/authActions';
import {
  TRACK_PERSONA_ANSWERS,
  TRACK_TUTORIAL_OPEN,
  TRACK_OPEN_ACCOUNT_MANAGEMENT,
  TRACK_TEMPLATE_PREVIEW_CLICK,
  TRACK_TEMPLATE_SCROLL,
  TRACK_TEMPLATE_EDIT,
  TRACK_DOWNLOAD_FORMAT_SELECTION_CHANGE,
  TRACK_CURSOR_SELECTION_CHANGE,
  TRACK_CURSOR_FILTER_CHANGE,
  TRACK_UPDATE_SCRIBE_BACKGROUND_SETTINGS,
  trackOpenUpgradeModal
} from 'JS/actionCreators/trackingActions';
import { DOWNLOAD_REQUEST } from 'JS/actionCreators/downloadActions';
import { SHOW_USERPERSONA_MODAL } from 'JS/actionCreators/marketingActions';
import { ADD_SCRIBE_SUCCESS, UPDATE_SCRIBE_CURSOR, CREATE_SHAPE_ELEMENT } from 'JS/actionCreators/scribeActions';
import { ROUTE_CODES } from 'JS/config/routes';
import { CREATE_SCRIBE_FROM_TEMPLATE } from 'JS/actionCreators/templatesActions';
import getFileTypeFromFormat from 'JS/shared/helpers/getFileTypeFromFormat';
import { DUPLICATE_SCRIBE } from 'JS/actionCreators/duplicateScribeActions';

import {
  track,
  identify,
  setUserProperties,
  setUserPropertiesOnce,
  registerEventSuperProperties,
  registerEventSuperPropertiesOnce,
  reset,
  incrementUserProperties,
  incrementEventSuperProperty
} from './mixpanelProvider';
import trackUpgradeModalActions from './saga/trackUpgradeModalActions';
import trackDownloadRequests from './saga/trackDownloadRequests';
import trackImagesSagas from './saga/trackImagesSagas';
import trackUiSagas from './saga/trackUiSagas';
import trackAudioSagas from './saga/trackAudioSagas';
import trackTimelineSagas from './saga/trackTimelineSagas';
import trackScribeListSagas from './saga/trackScribeListSagas';
import trackCanvasEvents from './saga/trackCanvasEvents';
import trackScenesSagas from './saga/trackScenesSagas';
import trackImageCroppingSagas from './saga/trackImageCroppingSagas';
import trackDuplicateScribeSaga from './saga/trackDuplicateScribeSaga';
import trackFastSpring from './saga/trackFastSpring';
import trackTemplateLoadedSaga from './saga/trackTemplateLoadedSaga';
import trackElementCursorSelectionSagas from './saga/trackElementCursorSelection';

export function* trackLogin({ user, type }) {
  yield call(identify, user.id);
  yield call(setUserProperties, {
    $email: user.email,
    'Subscription Tier': user.accountType,
    'Licence Type': user.accountType
  });
  yield call(registerEventSuperProperties, {
    'Subscription Tier': user.accountType,
    'Licence Type': user.accountType,
    'Subscription Term': user.term || ''
  });
  yield call(track, type === VALIDATE_CREDENTIALS_SUCCESS ? 'Logged In' : 'Re-auth');
}

export function* trackLogout() {
  yield call(track, 'Log Out');
  yield call(reset); // Clears super properties and generates a new random distinct_id for this instance.
}

export function* trackSignup({ email }) {
  const signupDate = new Date();
  const { user } = yield take(AUTH_SUCCESS);
  yield call(identify, user.id);
  yield call(setUserPropertiesOnce, {
    $email: email,
    'Signup Date': signupDate,
    'Subscription Tier': user.accountType,
    'Licence Type': user.accountType
  });
  yield call(registerEventSuperPropertiesOnce, {
    'Signup Date': signupDate,
    'Subscription Tier': user.accountType,
    'Licence Type': user.accountType
  });
  yield call(track, 'Account Created');
}

export function* trackPersonaAnswers({ answers, date, userTriggered }) {
  if (userTriggered) {
    yield call(track, 'Select User Org', { type: answers.user_type });
    yield call(track, 'Select User Intent', { intention: answers.intention });
  }
  yield call(setUserPropertiesOnce, {
    'Organisation Type': answers.user_type,
    Intentions: answers.intention,
    'Completed User Persona Form at': date
  });
  yield call(registerEventSuperPropertiesOnce, {
    'Organisation Type': answers.user_type,
    Intentions: answers.intention
  });
}

function* trackPersonaModalOpen() {
  yield call(track, 'UserPers Modal Shown');
}

function* trackTutorialOpen() {
  yield call(track, 'Launch Tutorial');
}

function* trackOpenAccountManagement() {
  yield call(track, 'Open Acc Manage');
}

function* trackNewScribeCreated({ scribe }) {
  if (scribe.source?.type === 'blank') {
    yield call(track, 'Create Blank Scribe', {
      'Canvas Size': scribe.canvasSize,
      'Scribe Type': scribe.source.type,
      'Scribe ID': scribe.id
    });
  }

  yield call(incrementUserProperties, {
    'Total Scribes Created': 1,
    [scribe.source?.type === 'template' ? 'Templates Opened' : 'Blank Scribes Created']: 1
  });
  yield call(incrementEventSuperProperty, 'Total Scribes Created');
  yield call(
    incrementEventSuperProperty,
    scribe.source?.type === 'template' ? 'Templates Opened' : 'Blank Scribes Created'
  );
}

function* trackNewScribeClicked() {
  yield call(track, 'Create New Scribe');
}

function* trackLocationChange({ payload }) {
  const { pathname } = payload.location;
  switch (true) {
    case pathname === ROUTE_CODES.SCRIBE_NEW:
      return yield call(trackNewScribeClicked);
    case pathname.includes(ROUTE_CODES.UPGRADE_LEGACY):
      return yield call(trackUpgradeModalActions, pathname);
    case pathname === ROUTE_CODES.HOME:
      return yield call(track, 'Select Templates Tab');
    case pathname === ROUTE_CODES.MY_SCRIBES:
      return yield call(track, 'Select My Scribes Tab');
    default:
      return;
  }
}

function* trackTemplatePreviewClick({ template, category }) {
  yield call(track, 'Preview Template', {
    'Template Title': template.title,
    'Template Category': category
  });
}

function* trackTemplateScroll({ category }) {
  yield call(track, 'Scroll Templates', {
    'Template Category': category
  });
}

function* trackTemplateEdit({ title, category, triggerLocation }) {
  const eventProperties = {
    'Template Title': title,
    'Template Category': category,
    'Button Location': triggerLocation
  };

  yield call(track, 'Template Requested', eventProperties);
  yield trackTemplateLoadedSaga(eventProperties);

  const { scribe } = yield take(ADD_SCRIBE_SUCCESS);

  yield call(track, 'Edit Template', {
    ...eventProperties,
    'Scribe ID': scribe.id
  });
}

function* trackTemplateEditFromPreview({ template, category }) {
  yield take(LOCATION_CHANGE); // Discard first LOCATION_CHANGE action
  const nextAction = yield take([CREATE_SCRIBE_FROM_TEMPLATE, LOCATION_CHANGE]);

  if (nextAction.type === CREATE_SCRIBE_FROM_TEMPLATE) {
    yield call(trackTemplateEdit, { title: template.title, category, triggerLocation: 'Template Preview Modal' });
  }
  if (nextAction.type === LOCATION_CHANGE && nextAction.payload?.location?.pathname?.includes(ROUTE_CODES.UPGRADE)) {
    const trigger = template.isPremium ? 'Premium Template' : 'Edit Template';

    yield put(
      trackOpenUpgradeModal(trigger, {
        'Template Title': template.title,
        'Template Category': category.title,
        'Template Button Location': 'Template Preview Modal'
      })
    );
  }
}

function* trackDownloadFormatSelectionChange({ format, scribe }) {
  const fileType = getFileTypeFromFormat(format);
  yield call(track, `Select ${fileType} Format`, {
    'Scribe ID': scribe.id,
    'Scribe Type': scribe.source?.type || 'unknown'
  });
}

function* trackCursorSelectionChange({ cursorId, scribeId }) {
  const cursor = yield select(({ scribes }) => scribes.cursors.find(c => c.id === cursorId));
  yield call(track, 'Select Default Cursor', {
    'Cursor Category': cursor?.group || 'No category',
    'Cursor Name': cursor?.name || 'No hand',
    'Scribe ID': scribeId
  });
}

function* trackCursorFilterChange({ category, scribeId }) {
  yield call(track, 'Select Default Cursor Category', { 'Cursor Category': category, 'Scribe ID': scribeId });
}

function* trackUpdateScribeCursor({ cursorId, scribeId }) {
  const cursor = yield select(({ scribes }) => scribes.cursors.find(c => c.id === cursorId));
  yield call(track, 'Confirm Default Cursor Selection', {
    'Cursor Category': cursor?.group || 'No category',
    'Cursor Name': cursor?.name || 'No hand',
    'Scribe ID': scribeId
  });
}

function* trackCreateShapeElement({ shapeType, scribeId }) {
  yield call(track, 'Add Shape To Canvas', { Shape: shapeType, 'Scribe ID': scribeId });
}

function* trackUpdateScribeBackgroundSettings({ scribeId, colorType, gradientType }) {
  yield call(track, 'Update Background Settings', {
    'Scribe ID': scribeId,
    'Colour Type': colorType,
    'Gradient Type': gradientType
  });
}

export default function* mixpanelSagas() {
  yield takeEvery([VALIDATE_CREDENTIALS_SUCCESS, VALIDATE_TOKEN_SUCCESS], trackLogin);
  yield takeEvery(LOGOUT, trackLogout);
  yield takeEvery(SIGNUP_SUCCESS, trackSignup);
  yield takeEvery(TRACK_PERSONA_ANSWERS, trackPersonaAnswers);
  yield takeEvery(DOWNLOAD_REQUEST, trackDownloadRequests);
  yield takeEvery(SHOW_USERPERSONA_MODAL, trackPersonaModalOpen);
  yield takeEvery(TRACK_TUTORIAL_OPEN, trackTutorialOpen);
  yield takeEvery(TRACK_OPEN_ACCOUNT_MANAGEMENT, trackOpenAccountManagement);
  yield takeEvery(ADD_SCRIBE_SUCCESS, trackNewScribeCreated);
  yield takeEvery(LOCATION_CHANGE, trackLocationChange);
  yield takeEvery(TRACK_TEMPLATE_PREVIEW_CLICK, trackTemplatePreviewClick);
  yield takeEvery(TRACK_TEMPLATE_SCROLL, trackTemplateScroll);
  yield takeEvery(TRACK_TEMPLATE_EDIT, trackTemplateEdit);
  yield takeEvery(TRACK_TEMPLATE_PREVIEW_CLICK, trackTemplateEditFromPreview);
  yield takeEvery(TRACK_DOWNLOAD_FORMAT_SELECTION_CHANGE, trackDownloadFormatSelectionChange);
  yield takeEvery(TRACK_CURSOR_SELECTION_CHANGE, trackCursorSelectionChange);
  yield takeEvery(TRACK_CURSOR_FILTER_CHANGE, trackCursorFilterChange);
  yield takeEvery(UPDATE_SCRIBE_CURSOR, trackUpdateScribeCursor);
  yield takeEvery(CREATE_SHAPE_ELEMENT, trackCreateShapeElement);
  yield takeEvery(DUPLICATE_SCRIBE, trackDuplicateScribeSaga);
  yield fork(trackImagesSagas);
  yield fork(trackUiSagas);
  yield takeEvery(TRACK_UPDATE_SCRIBE_BACKGROUND_SETTINGS, trackUpdateScribeBackgroundSettings);
  yield fork(trackAudioSagas);
  yield fork(trackTimelineSagas);
  yield fork(trackScribeListSagas);
  yield fork(trackCanvasEvents);
  yield fork(trackScenesSagas);
  yield fork(trackImageCroppingSagas);
  yield fork(trackFastSpring);
  yield fork(trackElementCursorSelectionSagas);
}
